/* import __COLOCATED_TEMPLATE__ from './lsg-select.hbs'; */
import $ from 'jquery';
import EmberObject, { action, computed } from '@ember/object';
import { scheduleOnce } from '@ember/runloop';
import { A } from '@ember/array';
import Component from '@ember/component';
import {
  KEY_CODES,
  KEY_CODES__ARROWS_UP_AND_DOWN
} from "../../constants/ui";
import { stopClickEvent } from "../../utils/events";

export const LSG_SELECT__PLACEHOLDER_SENTINEL = '****placeholder-value****';
/**
 * @type {string}     This will signify that we have a grouped option heading in our flattened list of options.
 */
const GROUPED_OPTION_VALUE_SENTINEL = 'GROUPED_';

export default Component.extend({

  /**
   * @inheritDoc
   */
  classNames: ['LSG-c-select'],

  /**
   * @inheritDoc
   */
  classNameBindings: [
    'showPlaceholderAsOptionsHeader',
    'isHasError:SH-u-has-error',
    'isActive:SH-u-is-active',
    'isDisabled:SH-u-is-disabled',
    'isShowDropdownCaret::LSG-c-select--hide-dropdown-caret',
    'useCustomCaretPositioning:LSG-c-select--custom-caret-position-active'
  ],

  /**
   * Set this to true to disable the enter multi-select
   *
   * @property {Boolean}
   */
  isDisabled: false,

  /**
   * Whether typing is disabled in the input field. Setting as true essentially turns this component
   * into a normal <select> element where the user is not allowed to type.
   *
   * @property {Boolean}
   */
  isTypingDisabled: false,

  /**
   * Property bound to the Glimmer 'has-error' classname.  Intended
   * to be used in conjunction with a passed-in property to more simply
   * propagate error states.
   *
   * @property {boolean}
   */
  isHasError: false,

  /**
   * Property bound to the Glimmer 'is-active' classname.  Intended
   * to be used in conjunction with a passed-in property to more simply
   * propagate error states.
   *
   * @property {boolean}
   */
  isActive: false,

  /**
   * Set this to false if you want to hide the dropdown caret altogether.
   *
   * @param {boolean}
   */
  isShowDropdownCaret: true,

  /**
   * Serves as the <select>'s value at any given time.  Intended
   * to be used in conjunction with a passed-in property to
   * more simply propagate values, with a (mut) or otherwise
   *
   *@property {string}
   */
  selectValue: undefined,

  /**
   * Materialize will automatically show your placeholder text
   * as the first option in the dropdown list.  If false,
   * that first option will be set to display:none
   *
   * @deprecated -- TODO - needs to be implemented if used.
   * @property {boolean}
   */
  showPlaceholderAsOptionsHeader: false,

  /**
   * When the select is initialized with materialize, an <input> element
   * will be generated which serves as the "dropdown" button.  If you want
   * that button to be populated with some kind of placeholder/hint text,
   * this is the field for you.
   *
   * @property {string}
   */
  placeholderText: undefined,

  /**
   * With this component, instead of specifying the <option> elements within
   * the template, you may pass an array of objects to populate the
   * options.  Should be of the form:
   *
   * options = [
   *  {
   *    title: 'Option 1',
   *    value: 1,
   *    selected: false (set to true if you want the item to be pre-selected),
   *    disabled: false (set to true if you want the item to be disabled),
   *    default: false (set to true if you want the option to be selected when an upstream value is set to null/undefined)
   *    reset: false (set to true if you want the select to reset to either default or unset state when this option is selected),
   *    tooltip: Optional. text to show on hover of the chip. TODO -- implement for non-chip descendents.
   *  },
   *    ...
   * ];
   *
   * @property {array}
   */
  options: undefined,

  /**
   * groupedOptions = [{
   *  groupName: 'regions',
   *  groupTitle: 'Regions of Justice',
   *  options: [{  ***** 'options' Must be a Ember.MutableArray *******
   *    title: 'Europe',
   *    value: '3',
   *    selected: false,
   *    disabled: false,
   *    reset, false
   *  }, ...]
   *},{
   *  groupName: 'countries',
   *  groupTitle: 'Countries of Yore',
   *  options: [{ ***** 'options' Must be a Ember.MutableArray *******
   *    ...
   *  }, ...]
   *},
   *  ...
   *]
   *
   * @param {Ember.MutableArray}    See format above
   */
  groupedOptions: A(),

  /**
   * Action is called with the selected value during 'onChange' events
   */
  action: undefined,

  /**
   * e.g. return string value 'countries|3' or the action option Object.
   *
   * @param {Boolean}   True if you want the action function to return a full 'option' object.
   *                    False will just return the string value.
   */
  isActionReturnsFullOption: false,

  /**
   * If this value is selected then we reset the drop down (this is probably only needed for filter lists)
   */
  resetValue: undefined,

  /**
   * Note: THIS FEATURE IS EXPERIMENTAL!!! USE AT OWN RISK!
   *
   * Intended to be passed-in by the template.  When enabled, instead of having
   * the dropdown caret positioned on the far-right, we attempt to have it positioned
   * just to the left of the text
   *
   * @property {boolean}
   */
  useCustomCaretPositioning: false,

  /**
   * Shows a checkmark next to the currently selected dropdown option.
   * Requires that options.selected be defined.
   *
   * @property {boolean}
   */
  markSelectedDropdownOption: false,

  /**
   * @inheritDoc
   */
  didReceiveAttrs() {
    // Update visually selected value
    this._setSelectElToSelectValue();

    if (this.get('useCustomCaretPositioning')) {
      scheduleOnce('afterRender', this, function() {
        this._positionCaret();
      });
    }
  },

  /**
   * Set the visually selected element to match the internal value state.
   *
   * @private
   */
  _setSelectElToSelectValue() {
    let selectedValue = this.get('selectValue');
    const allOptions = this.get('_flattenedOptions');

    if (allOptions.length < 1) {
      return;
    }

    if (selectedValue === undefined) {
      // We technically have no selected value. This is an invalid state so let's try to pick the best option
      const bestOption = allOptions.find(option => {
        return option.get('selected');
      });

      selectedValue = bestOption ? bestOption.get('value') : allOptions.get('firstObject').get('value');
      this.set('selectValue', selectedValue);
    }

    const selectedOption = allOptions.find(option => {
        return option.get('value') == selectedValue;
      });

    if (selectedOption && !selectedOption.get('selected')) {
      allOptions.forEach(option => {
        option.set('selected', false);
      });

      selectedOption.set('selected', true);
    }
  },

  /**
   * @type {Boolean}   true if we have grouped options.
   * @private
   */
  _hasGroupedOptions: computed('groupedOptions.[]', function() {
    return this.get('groupedOptions.length') > 0;
  }),

  /**
   * Attempt to calculate the width/height of the text in the select-dropdown
   * <input> and reposition the caret based on those values
   *
   * @private
   */
  _positionCaret() {
    let selectValue = this.get('_selectedOptionToShow.title'),
      selectInput = $(this.element).find('.LSG-c-select--container--trigger--input input'),
      caret = $(this.element).find('.LSG-c-select--container--trigger--caret');

    if (!selectValue) {
      return;
    }

    // Create a temporary element for performing calculations
    let hiddenSpan = $('<span>')
      .text(selectValue)
      .css('font-family', selectInput.css('font-family'))
      .css('font-size', selectInput.css('font-size'))
      .css('line-height', selectInput.css('line-height'))
      .hide().appendTo($('body'));

    let width = hiddenSpan.width();

    hiddenSpan.remove();

    // The following adjustments are just constants found to be consistently
    // useful for positioning the Materialze Caret, using its default styles
    let leftAdjustment = 4;

    // Adjust the position of the caret
    caret.css({
      left: `${width + leftAdjustment}px`,
    });
  },

  /**
   * @type {String}
   */
  PLACEHOLDER_SENTINEL: LSG_SELECT__PLACEHOLDER_SENTINEL,

  /**
   * @type {Number}     This is the selected index if the arrow key was used to selected an option.
   */
  _arrowKeySelectedIndex: -1,

  /**
   * This is a flattened array of options.  So, in the case of grouped options we will have a single array
   * of all options with the grouped option headings being specified by a value of the GROUPED_OPTION_VALUE_SENTINEL
   * as the first part of the option.
   *
   * @type {Ember.A<Object>}
   */
  _flattenedOptions: computed('options.[]', function() {
    let allOptions = A();

    if (this.get('options.length')) {
      this.get('options').forEach(option => {
        if (option.get) {
          return;
        }

        let emberOption = EmberObject.create(option);
        allOptions.pushObject(emberOption);
      });

      return allOptions;
    }

    if (!this.get('groupedOptions.length')) {
      return A();
    }

    this.get('groupedOptions').forEach(groupedOption => {
      groupedOption.value = `${GROUPED_OPTION_VALUE_SENTINEL}${groupedOption.groupName}`;
      allOptions.pushObject(EmberObject.create(groupedOption));
      groupedOption.options.forEach(option => {
        if (option.get) {
          return;
        }

        let emberOption = EmberObject.create(option);
        allOptions.pushObject(emberOption);
      });
    });

    return allOptions;
  }),

  /**
   * @type {Ember.Object}   The option that has been selected.
   */
  _selectedOptionToShow: computed(
  '_flattenedOptions.@each.{title,value,selected}',
  function() {
    let options = this.get('_flattenedOptions'),
      selectedOption = options.find(option => {
        return option.get('selected');
      });

    if (selectedOption) {
      return selectedOption;
    }

    selectedOption = options.find(option => {
      return option.get('selected');
    });

    if (selectedOption) {
      return selectedOption;
    }

    let defaultOption = options.find(option => {
      return option.get('default');
    });

    if (defaultOption) {
      return defaultOption;
    }

    return options.objectAt(0);
  }),

  /**
   * @param {Ember.Object} optionSelected
   * @private
   */
  _sendSelectedOption(optionSelected) {
    //if we have an action and we want the full option, fire this to callers.
    const action = this.get('action');
    if (action) {
      action(this.get('isActionReturnsFullOption') ? optionSelected : optionSelected.get('value'));
    }

    this.set('_arrowKeySelectedIndex', -1);
    this.set('selectValue', optionSelected.get('value'));
  },

  handleScrollSelectionIntoView: action(function() {
    const parentScrollEl = $(this.element).find('.ember-basic-dropdown-content');
    const scrollEl = $(this.element).find('.LSG-c-select--container--content');
    const parentScrollElHeight = parentScrollEl.height();
    const optionHeight = scrollEl.find('.LSG-c-select--container--content--item').height();
    const arrowSelectedIndex = this._arrowKeySelectedIndex;
    const arrowLocationPixels = arrowSelectedIndex * optionHeight;

    if (!parentScrollEl.offset() || !scrollEl.offset()) {
      return;
    }

    const offsetTop = parentScrollEl.offset().top - scrollEl.offset().top;
    const offsetBottom = offsetTop + parentScrollElHeight;

    if (offsetTop > arrowLocationPixels) {
      scheduleOnce('afterRender', this, function() {
        parentScrollEl.scrollTop(arrowLocationPixels);
      });
    }

    if (arrowLocationPixels >= offsetBottom) {
      scheduleOnce('afterRender', this, function() {
        parentScrollEl.scrollTop(arrowLocationPixels - parentScrollElHeight + optionHeight);
      });
    }
  }),

  handleArrowNavigation: action(function(key) {
    const currentlySelectedArrowIndex = this._arrowKeySelectedIndex;
    const options = this._flattenedOptions;
    const optionsLength = options.length;
    let nextArrowIndex = Math.min(currentlySelectedArrowIndex + 1, optionsLength - 1);

    if (key === KEY_CODES.ARROW_UP) {
      nextArrowIndex = Math.max(currentlySelectedArrowIndex - 1, 0);
    }

    const nextSelectedOption = options.objectAt(nextArrowIndex);
    const groupedRegex = new RegExp(`^${GROUPED_OPTION_VALUE_SENTINEL}`);
    if (groupedRegex.test(nextSelectedOption.value)) {
      nextArrowIndex = Math.min(currentlySelectedArrowIndex + 2, optionsLength - 1);

      if (key === KEY_CODES.ARROW_UP) {
        nextArrowIndex = Math.max(currentlySelectedArrowIndex - 2, 0);
      }
    }

    this.set('_arrowKeySelectedIndex', nextArrowIndex);
    this.handleScrollSelectionIntoView();
  }),

  handleEnterClicked: action(function(dropdown) {
    const currentlySelectedArrowIndex = this._arrowKeySelectedIndex;
    if (currentlySelectedArrowIndex < 0) {
      dropdown.actions.open();
      return;
    }

    const allOptions = this._flattenedOptions;
    const selectedOption = allOptions.objectAt(currentlySelectedArrowIndex);

    allOptions.forEach(option => {
      option.set('selected', false);
    });

    selectedOption.set('selected', true);
    this._sendSelectedOption(selectedOption);
  }),

  actions: {
    /**
     * @param {Dropdown} dropdown
     * @param {MouseEvent} event
     */
    handleKeyDown(dropdown, event) {
      let key = event.which || event.keyCode || 0;
      if (key === KEY_CODES.ENTER) {
        stopClickEvent(event);
        this.handleEnterClicked(dropdown);
        return;
      }

      if (key === KEY_CODES.ESC) {
        stopClickEvent(event);
        this.set('_arrowKeySelectedIndex', -1);
        return;
      }

      if (KEY_CODES__ARROWS_UP_AND_DOWN.includes(key)) {
        stopClickEvent(event);
        this.handleArrowNavigation(key);
        return;
      }
    },

    /**
     * Handle the user has selected an option with the mouse.
     *
     * @param {Dropdown} dropdown
     * @param {Ember.Object} option
     */
    onDropdownItemSelected(dropdown, option) {
      dropdown.actions.close();

      let allOptions = this.get('_flattenedOptions');
      allOptions.forEach(option => {
        option.set('selected', false);
      });

      option.set('selected', true);
      this._sendSelectedOption(option);
    }
  }
});
